import { useCallback, useEffect, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';

const TOKEN_KEY = 'activeid_token';

export interface AiStreamDto {
  kind: 'token' | 'tool' | 'done' | 'error';
  text?: string | null;
  toolName?: string | null;
  conversationId: number;
  error?: string | null;
}

export interface AiStreamHandlers {
  onToken: (text: string) => void;
  onTool: (toolName: string) => void;
  onDone: (conversationId: number) => void;
  onError: (message: string) => void;
}

export interface AiAskRequest {
  conversationId: number | null;
  message: string;
  /** What the user is currently looking at, so "explain this" resolves to the page. */
  pageContext?: string | null;
}

function createConnection(): HubConnection {
  const base = import.meta.env.VITE_API_URL || '';
  return new HubConnectionBuilder()
    .withUrl(`${base}/ai/stream`, {
      // SignalR appends this as ?access_token= on the socket; the API's JWT
      // OnMessageReceived handler reads it for /ai/stream.
      accessTokenFactory: () => localStorage.getItem(TOKEN_KEY) ?? '',
    })
    .withAutomaticReconnect()
    .build();
}

/**
 * Streams an "Ask ActiveID" turn over the /ai/stream SignalR hub. Maintains a single
 * lazily-started connection for the component's lifetime. `ask` resolves when the
 * server stream completes; if the hub is unreachable (e.g. AI disabled → no hub),
 * onError fires with 'unavailable'.
 */
export function useAiStream() {
  const connRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    return () => {
      connRef.current?.stop().catch(() => undefined);
      connRef.current = null;
    };
  }, []);

  const ensureStarted = useCallback(async (): Promise<HubConnection> => {
    if (!connRef.current) connRef.current = createConnection();
    const conn = connRef.current;
    if (conn.state === HubConnectionState.Disconnected) await conn.start();
    return conn;
  }, []);

  const ask = useCallback(
    async (request: AiAskRequest, handlers: AiStreamHandlers): Promise<void> => {
      let conn: HubConnection;
      try {
        conn = await ensureStarted();
      } catch {
        handlers.onError('unavailable');
        return;
      }

      await new Promise<void>((resolve) => {
        conn
          .stream('Ask', {
            conversationId: request.conversationId,
            message: request.message,
            pageContext: request.pageContext ?? null,
          })
          .subscribe({
            next: (dto: AiStreamDto) => {
              if (dto.kind === 'token' && dto.text) handlers.onToken(dto.text);
              else if (dto.kind === 'tool' && dto.toolName) handlers.onTool(dto.toolName);
              else if (dto.kind === 'done') handlers.onDone(dto.conversationId);
              else if (dto.kind === 'error') handlers.onError(dto.error || 'error');
            },
            complete: () => resolve(),
            error: (err: unknown) => {
              handlers.onError(err instanceof Error ? err.message : 'stream error');
              resolve();
            },
          });
      });
    },
    [ensureStarted],
  );

  return { ask };
}
